import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  MailOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Badge, Button, Tooltip } from "antd";
import useNavigateWithSearch from "shared/hooks/useNavigateWithSearch";
import { MarketingMaterialTableItem } from "shared/types/marketingMaterials";
import { useMarketingMaterialsContext } from "../marketingMaterials/MarketingMaterialsProvider";
import { useMarketingMaterialActions } from "../marketingMaterials/hooks/useMarketingMaterialActions";
import { ROUTES } from "../marketingMaterials/utils/constants";
import { getMaterialBadge } from "../marketingMaterials/utils/getMarketingMaterialBadge";
import styles from "./DrawerFooter.module.scss";

type Props = {
  isDownloading?: boolean;
  isSaving: boolean;
  type: "edit" | "create" | "archive";
  onClose: () => void;
  onPrint?: (cb: () => void) => Promise<void>;
  onDownload?: () => void;
  onSendEmail?: () => void;
  onSave: () => void;
};

const DrawerFooter = ({
  isSaving,
  isDownloading,
  type,
  onSendEmail,
  onClose,
  onDownload,
  onPrint,
  onSave,
}: Props) => {
  const navigateWithSearch = useNavigateWithSearch();
  const { onDuplicate, onDelete, isCreating, isDeleting } =
    useMarketingMaterialActions();

  const { material, disclosureFieldId, initialValues } =
    useMarketingMaterialsContext();

  const errorStatus =
    material?.templateErrorStatus ?? material?.materialErrorStatus;

  const materialStatus = getMaterialBadge(
    material as MarketingMaterialTableItem,
  );

  const disabled =
    !!(
      initialValues?.fields?.[disclosureFieldId || ""] &&
      material?.materialErrorStatus
    ) || !!material?.templateErrorStatus;

  const tooltipVisible = disabled ? undefined : false;

  return (
    <div className={styles.footer}>
      {type === "edit" && material ? (
        <div>
          <Button
            type="default"
            danger
            htmlType="button"
            loading={isDeleting}
            onClick={() => {
              onDelete({
                ids: [material.id],
                onSuccess: () => {
                  navigateWithSearch(ROUTES.home);
                  onClose();
                },
              });
            }}
          >
            <DeleteOutlined />
            Delete
          </Button>
          <Tooltip visible={tooltipVisible} title={errorStatus?.message}>
            <Button
              disabled={!!errorStatus}
              type="default"
              htmlType="button"
              loading={isCreating}
              onClick={() => {
                onDuplicate({
                  material,
                  onSuccess: ({ id }) => {
                    navigateWithSearch(ROUTES.edit(id));
                  },
                });
              }}
            >
              <CopyOutlined />
              Duplicate
            </Button>
          </Tooltip>
          {onSendEmail && (
            <Tooltip visible={tooltipVisible} title={errorStatus?.message}>
              <Button
                disabled={
                  !!errorStatus ||
                  !material.templateDeliveryMethods?.includes("email")
                }
                type="default"
                htmlType="button"
                onClick={onSendEmail}
              >
                <MailOutlined />
                Email
              </Button>
            </Tooltip>
          )}
          {onDownload && (
            <Button
              disabled={
                !!errorStatus ||
                isDownloading ||
                !material.templateDeliveryMethods?.includes("download")
              }
              type="default"
              htmlType="button"
              onClick={onDownload}
            >
              <DownloadOutlined />
              Download
            </Button>
          )}
          <Tooltip
            overlayStyle={{ whiteSpace: "pre-line", maxWidth: "348px" }}
            title={undefined}
          >
            <Button
              type="default"
              htmlType="button"
              onClick={() =>
                onPrint?.(() =>
                  navigateWithSearch(ROUTES.print(material.id), {
                    state: {
                      origin: "DRAWER",
                    },
                  }),
                )
              }
              disabled={
                !onPrint || !material.templateDeliveryMethods?.includes("print")
              }
            >
              <PrinterOutlined />
              Order Prints
            </Button>
          </Tooltip>
          {materialStatus && (
            <div className="material-status">
              <Tooltip title={materialStatus.tooltipInfo}>
                <Badge
                  color={materialStatus.color}
                  text={materialStatus.title}
                />
              </Tooltip>
            </div>
          )}
        </div>
      ) : (
        <div />
      )}

      <div>
        <Button type="default" htmlType="button" onClick={onClose}>
          Close
        </Button>
        <Tooltip
          visible={tooltipVisible}
          title={errorStatus?.message}
          placement="topRight"
        >
          {type !== "archive" && (
            <Button
              type="primary"
              htmlType="button"
              onClick={onSave}
              loading={isSaving}
              disabled={disabled}
            >
              Save
            </Button>
          )}
        </Tooltip>
      </div>
    </div>
  );
};

export default DrawerFooter;
